import React, { Fragment, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Dialog, Transition } from "@headlessui/react";
import {
  HiCheck,
  HiOutlineExclamationCircle,
  HiOutlineSparkles,
  HiOutlineX,
} from "react-icons/hi";
import {
  AI_ANALYSE_DISPLAY_STAGES,
  resolveActiveStageIndex,
} from "../aiAnalyseStages";
import { releaseBodyScrollLockAfterTransition } from "../utils/bodyScrollLock";
import { useAiProgressDisplay } from "../utils/useAiProgressDisplay";
import AiAnalyseLottiePlayer from "./AiAnalyseLottiePlayer";

const TimesheetAiProgressModal = ({
  isOpen,
  percent = 0,
  stage = "starting",
  label = "Starting AI analyse",
  timesheetCompleted = 0,
  timesheetIndex = 0,
  timesheetTotal = 0,
  timesheetLabel = "",
  error = null,
  onClose,
  onCancelRequest,
}) => {
  const hasError = Boolean(error);
  const isFinishing = stage === "finalising" || stage === "complete";
  const displayPercent = useAiProgressDisplay(percent, isOpen && !hasError);
  const safePercent = Math.max(
    0,
    Math.min(100, Math.round(displayPercent * 10) / 10)
  );
  const displayPercentInt = Math.round(safePercent);
  const activeIndex = resolveActiveStageIndex(stage);
  const completedCount = Math.max(
    0,
    Number.isFinite(Number(timesheetCompleted))
      ? Number(timesheetCompleted)
      : Number(timesheetIndex) || 0
  );
  const showBatchLine = timesheetTotal > 1;
  const allStepsComplete = stage === "complete" || safePercent >= 99;

  const statusLabel = useMemo(() => {
    if (hasError) return label;
    if (stage === "complete") return "Analyse complete";
    if (safePercent >= 99 && safePercent < 100) return "Almost done…";
    if (isFinishing) return label || "Finalising analyse report";
    return label;
  }, [hasError, isFinishing, label, safePercent, stage]);

  const handleDismiss = useCallback(async () => {
    if (hasError) {
      onClose?.();
      return;
    }
    if (onCancelRequest) {
      await onCancelRequest();
      return;
    }
    onClose?.();
  }, [hasError, onCancelRequest, onClose]);

  // Headless UI Dialog manages overflow; afterLeave clears inert + any stale lock.
  const content = (
    <Transition
      show={isOpen}
      as={Fragment}
      afterLeave={releaseBodyScrollLockAfterTransition}
    >
      <Dialog
        as='div'
        className='relative z-[10100]'
        open={isOpen}
        onClose={() => {}}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-200'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-150'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-slate-900/55 backdrop-blur-[3px]' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto p-3 sm:p-4'>
          <div className='flex min-h-full items-center justify-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-200'
              enterFrom='opacity-0 translate-y-2 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-150'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-2 sm:scale-95'
            >
              <Dialog.Panel className='w-full max-w-xl transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl ring-1 ring-black/5 sm:max-w-2xl'>
                <div className='relative overflow-hidden bg-gradient px-4 py-3 text-white'>
                  <div className='relative flex items-start justify-between gap-2'>
                    <div className='flex min-w-0 items-center gap-2.5'>
                      <span className='inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 ring-1 ring-inset ring-white/20'>
                        <HiOutlineSparkles className='h-4 w-4' />
                      </span>
                      <div className='min-w-0'>
                        <Dialog.Title
                          as='h2'
                          className='text-base font-semibold leading-tight sm:text-lg'
                        >
                          Analysing with AI
                        </Dialog.Title>
                        <p className='mt-0.5 line-clamp-2 text-xs text-blue-100/90'>
                          {hasError
                            ? "Something went wrong during analyse"
                            : stage === "complete"
                              ? "All checks finished — preparing your report"
                              : "Scanning timesheet data & running NDIS checks"}
                        </p>
                        {showBatchLine ? (
                          <p className='mt-1 truncate text-[11px] font-medium text-blue-100/75'>
                            {completedCount} of {timesheetTotal} complete
                          </p>
                        ) : timesheetLabel ? (
                          <p className='mt-1 truncate text-[11px] font-medium text-blue-100/75'>
                            {timesheetLabel}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <button
                      type='button'
                      onClick={handleDismiss}
                      className='shrink-0 rounded-lg p-1.5 text-white/85 transition hover:bg-white/10 hover:text-white'
                      aria-label={hasError ? "Close" : "Cancel analyse"}
                    >
                      <HiOutlineX className='h-5 w-5' />
                    </button>
                  </div>
                </div>

                <div className='px-4 py-3 sm:px-5'>
                  {hasError ? (
                    <div className='flex flex-col items-center py-2 text-center'>
                      <span className='inline-flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-500 ring-1 ring-red-100'>
                        <HiOutlineExclamationCircle className='h-7 w-7' />
                      </span>
                      <p className='mt-3 text-sm font-semibold text-red-900'>
                        Analyse could not complete
                      </p>
                      <p className='mt-1.5 max-w-sm text-sm leading-relaxed text-red-700/90'>
                        {error}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className='mb-2 flex items-center justify-center'>
                        <AiAnalyseLottiePlayer
                          height={240}
                          active={isOpen && !hasError}
                        />
                      </div>

                      <div className='mb-2 flex items-center justify-between gap-3'>
                        <p className='min-w-0 truncate text-sm font-semibold text-[#02397A]'>
                          {statusLabel}
                        </p>
                        <span
                          className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ring-1 ring-inset sm:text-sm ${
                            stage === "complete"
                              ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                              : "bg-[#ECF0FD] text-[#4B6CB7] ring-blue-100"
                          }`}
                        >
                          {displayPercentInt}%
                        </span>
                      </div>

                      <div className='relative h-2.5 overflow-hidden rounded-full bg-[#ECF0FD] ring-1 ring-inset ring-blue-100'>
                        <div
                          className='absolute inset-y-0 left-0 rounded-full bg-gradient will-change-[width]'
                          style={{ width: `${safePercent}%` }}
                        />
                        <div
                          className='absolute inset-y-0 left-0 overflow-hidden rounded-full opacity-60 will-change-[width]'
                          style={{ width: `${safePercent}%` }}
                        >
                          <div className='h-full w-[200%] -translate-x-1/4 animate-pulse bg-gradient-to-r from-white/0 via-white/45 to-white/0' />
                        </div>
                        {stage === "complete" ? (
                          <div className='absolute inset-y-0 right-1.5 flex items-center'>
                            <HiCheck className='h-3 w-3 text-white drop-shadow' />
                          </div>
                        ) : null}
                      </div>

                      <ul className='mt-3 space-y-0'>
                        {AI_ANALYSE_DISPLAY_STAGES.map((step, index) => {
                          const isDone = allStepsComplete
                            ? true
                            : index < activeIndex;
                          const isActive = !isDone && index === activeIndex;
                          const isLast =
                            index === AI_ANALYSE_DISPLAY_STAGES.length - 1;

                          return (
                            <li
                              key={step.key}
                              className='relative flex items-stretch gap-3'
                            >
                              <div className='flex w-4 shrink-0 flex-col items-center'>
                                <span
                                  className={`mt-3 h-2 w-2 shrink-0 rounded-full transition-all duration-300 ${
                                    isDone
                                      ? "bg-emerald-500 shadow-sm shadow-emerald-200"
                                      : isActive
                                        ? "h-2.5 w-2.5 bg-[#4B6CB7] ring-4 ring-[#ECF0FD]"
                                        : "bg-gray-300"
                                  }`}
                                />
                                {!isLast ? (
                                  <span
                                    className={`my-1 w-px flex-1 min-h-[20px] ${
                                      isDone ? "bg-emerald-200" : "bg-gray-200"
                                    }`}
                                    aria-hidden
                                  />
                                ) : null}
                              </div>

                              <div
                                className={`mb-2 flex min-w-0 flex-1 items-center justify-between gap-2 rounded-lg border px-3 py-2 transition-all duration-300 ${
                                  isActive
                                    ? "border-[#4B6CB7]/25 bg-[#ECF0FD]/80 shadow-sm"
                                    : isDone
                                      ? "border-emerald-100/80 bg-emerald-50/50"
                                      : "border-gray-100 bg-gray-50/40"
                                }`}
                              >
                                <span
                                  className={`text-sm ${
                                    isActive
                                      ? "font-semibold text-[#02397A]"
                                      : isDone
                                        ? "font-medium text-emerald-800"
                                        : "text-gray-500"
                                  }`}
                                >
                                  {step.label}
                                </span>
                                {isDone ? (
                                  <span className='inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-600'>
                                    <HiCheck className='h-3.5 w-3.5' />
                                    Done
                                  </span>
                                ) : isActive ? (
                                  <span className='inline-flex shrink-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#4B6CB7]'>
                                    <span className='inline-block h-3 w-3 animate-spin rounded-full border-2 border-blue-200 border-t-[#4B6CB7]' />
                                    Active
                                  </span>
                                ) : (
                                  <span className='shrink-0 text-[10px] font-medium uppercase tracking-wide text-gray-400'>
                                    Waiting
                                  </span>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>

                      <p className='mt-2 rounded-lg border border-dashed border-blue-100 bg-[#ECF0FD]/35 px-3 py-2 text-center text-[11px] leading-snug text-gray-600'>
                        {safePercent >= 99 && safePercent < 100
                          ? "Finishing up — your report will appear in a moment."
                          : "Keep this window open. Closing will ask you to confirm cancellation."}
                      </p>
                    </>
                  )}
                </div>

                {hasError ? (
                  <div className='border-t border-gray-100 bg-gray-50/80 px-4 py-3'>
                    <button
                      type='button'
                      onClick={onClose}
                      className='inline-flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50'
                    >
                      Close
                    </button>
                  </div>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );

  return createPortal(content, document.body);
};

export default TimesheetAiProgressModal;
